// UploadWizard — 4-step CSV upload → schema inference → mapping → preview → materialize.
// Lives inside a <Dialog> (max-w-2xl).
//
// Step 1: Upload — file picker + row-count cap check (> 50k → error, no upload)
// Step 2: Mapping — Object Type selection + auto-apply or manual wizard
// Step 3: Preview — coerced first 5 rows
// Step 4: Materialize — POST to materialize-objects edge function (async/polling via useMaterialization)

import { useState, useRef, useCallback } from "react";
import { UploadCloud, Check, AlertCircle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useUploadCsv } from "@/hooks/useDataSources";
import { useFindMappingBySignature } from "@/hooks/useMappings";
import { useMaterialization } from "@/hooks/useMaterializations";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import { inferSchemaFromText } from "@/lib/ontology/csvSchemaInference";
import { parseCSV } from "@/lib/backtest/csvParser";
import { MappingWizard } from "@/components/ontology/MappingWizard";
import { MaterializationPreview } from "@/components/ontology/MaterializationPreview";
import type { MaterializeObjectsResponse } from "@/types/edge-functions";
import type {
  DataSource,
  DataSourceSchema,
  Mapping,
  ObjectType,
  PropertyType,
} from "@/types/ontology";

// Maximum rows allowed before a client-side reject (per spec §8 + plan §4 Agent C checklist).
export const ROW_COUNT_CAP = 50_000;

/**
 * Exported for unit testing: returns an error message if rows exceeds the cap, null otherwise.
 * This is the exact guard used in handleFileChosen.
 */
export function checkRowCount(rowCount: number): string | null {
  if (rowCount > ROW_COUNT_CAP) {
    return (
      `This file has ${rowCount.toLocaleString()} rows, which exceeds the ${ROW_COUNT_CAP.toLocaleString()}-row limit. ` +
      `Please split the file or contact support for bulk import.`
    );
  }
  return null;
}

// ── Step indicator ──────────────────────────────────────────────────────────

type WizardStep = 1 | 2 | 3 | 4;

interface StepConfig {
  label: string;
}

const STEPS: StepConfig[] = [
  { label: "Upload" },
  { label: "Map" },
  { label: "Preview" },
  { label: "Materialize" },
];

function WizardStepper({ current }: { current: WizardStep }) {
  return (
    <div className="flex items-center gap-0 mb-6">
      {STEPS.map((step, idx) => {
        const stepNum = (idx + 1) as WizardStep;
        const isComplete = stepNum < current;
        const isActive = stepNum === current;
        return (
          <div key={step.label} className="flex items-center">
            <div className="flex flex-col items-center gap-1">
              <div
                className={[
                  "w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-medium",
                  isComplete
                    ? "bg-primary border-primary text-primary-foreground"
                    : isActive
                      ? "border-primary text-primary"
                      : "border-muted-foreground/40 text-muted-foreground",
                ].join(" ")}
              >
                {isComplete ? <Check className="w-3.5 h-3.5" /> : stepNum}
              </div>
              <span
                className={[
                  "text-xs whitespace-nowrap",
                  isActive ? "text-foreground font-medium" : "text-muted-foreground",
                ].join(" ")}
              >
                {step.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                className={["h-0.5 w-12 mx-1 mb-4", isComplete ? "bg-primary" : "bg-border"].join(
                  " "
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export interface UploadWizardProps {
  open: boolean;
  onClose: () => void;
}

interface ParsedCsv {
  headers: string[];
  rows: Record<string, string>[];
  schema: DataSourceSchema;
  file: File;
}

export function UploadWizard({ open, onClose }: UploadWizardProps) {
  const [step, setStep] = useState<WizardStep>(1);
  const [parsedCsv, setParsedCsv] = useState<ParsedCsv | null>(null);
  const [rowCountError, setRowCountError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [dataSource, setDataSource] = useState<DataSource | null>(null);
  const [selectedObjectType, setSelectedObjectType] = useState<ObjectType | null>(null);
  const [autoAppliedMapping, setAutoAppliedMapping] = useState<Mapping | null>(null);
  const [savedMapping, setSavedMapping] = useState<Mapping | null>(null);
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([]);

  const uploadCsv = useUploadCsv();
  const findMappingBySignature = useFindMappingBySignature();

  const handleClose = useCallback(() => {
    // Reset all state when closing.
    setStep(1);
    setParsedCsv(null);
    setRowCountError(null);
    setParsing(false);
    setDataSource(null);
    setSelectedObjectType(null);
    setAutoAppliedMapping(null);
    setSavedMapping(null);
    setPropertyTypes([]);
    onClose();
  }, [onClose]);

  const handleFileChosen = useCallback(async (file: File) => {
    setRowCountError(null);
    setParsing(true);

    try {
      const text = await file.text();
      const { headers, rows } = parseCSV(text);

      // Client-side row count cap — must reject before any upload or edge-function call.
      if (rows.length > ROW_COUNT_CAP) {
        setRowCountError(
          `This file has ${rows.length.toLocaleString()} rows, which exceeds the ${ROW_COUNT_CAP.toLocaleString()}-row limit. ` +
            `Please split the file or contact support for bulk import.`
        );
        setParsing(false);
        return;
      }

      const { schema } = await inferSchemaFromText(text);

      setParsedCsv({ headers, rows, schema, file });
    } catch (err) {
      setRowCountError(`Could not parse the CSV file: ${(err as Error).message}`);
    } finally {
      setParsing(false);
    }
  }, []);

  const handleUploadAndNext = useCallback(async () => {
    if (!parsedCsv) return;

    const ds = await uploadCsv.mutateAsync({
      file: parsedCsv.file,
      name: parsedCsv.file.name,
      schema: parsedCsv.schema,
      rowCount: parsedCsv.rows.length,
    });

    setDataSource(ds);
    setStep(2);
  }, [parsedCsv, uploadCsv]);

  // Called from MappingWizard when the user selects an Object Type (step 2 internal state).
  const handleObjectTypeSelected = useCallback(
    async (objectType: ObjectType, props: PropertyType[]) => {
      setSelectedObjectType(objectType);
      setPropertyTypes(props);

      // Signature-based auto-apply lookup.
      if (parsedCsv?.schema.header_signature) {
        try {
          const match = await findMappingBySignature(
            objectType.id,
            parsedCsv.schema.header_signature
          );
          if (match) {
            setAutoAppliedMapping(match);
            setSavedMapping(match);
            // Auto-apply: skip straight to preview.
            setStep(3);
            return;
          }
        } catch {
          // If lookup fails, fall through to the manual mapping step.
        }
      }

      setAutoAppliedMapping(null);
    },
    [parsedCsv, findMappingBySignature]
  );

  const handleMappingSaved = useCallback((mapping: Mapping) => {
    setSavedMapping(mapping);
    setStep(3);
  }, []);

  const handleMaterialize = useCallback(() => {
    setStep(4);
  }, []);

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Upload CSV Data Source</DialogTitle>
        </DialogHeader>

        <WizardStepper current={step} />

        {step === 1 && (
          <Step1Upload
            parsing={parsing}
            parsedCsv={parsedCsv}
            rowCountError={rowCountError}
            uploading={uploadCsv.isPending}
            onFileChosen={handleFileChosen}
            onNext={handleUploadAndNext}
          />
        )}

        {step === 2 && parsedCsv && dataSource && (
          <Step2Mapping
            parsedCsv={parsedCsv}
            dataSource={dataSource}
            autoAppliedMapping={autoAppliedMapping}
            propertyTypes={propertyTypes}
            selectedObjectType={selectedObjectType}
            onObjectTypeSelected={handleObjectTypeSelected}
            onMappingSaved={handleMappingSaved}
          />
        )}

        {step === 3 && parsedCsv && savedMapping && selectedObjectType && (
          <Step3Preview
            parsedCsv={parsedCsv}
            mapping={savedMapping}
            objectType={selectedObjectType}
            propertyTypes={propertyTypes}
            autoApplied={!!autoAppliedMapping}
            onEditMapping={() => {
              setAutoAppliedMapping(null);
              setStep(2);
            }}
            onMaterialize={handleMaterialize}
          />
        )}

        {step === 4 && parsedCsv && savedMapping && dataSource && selectedObjectType && (
          <Step4Materialize
            dataSource={dataSource}
            mapping={savedMapping}
            rowCount={parsedCsv.rows.length}
            onDone={handleClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Step 1: Upload ──────────────────────────────────────────────────────────

interface Step1Props {
  parsing: boolean;
  parsedCsv: ParsedCsv | null;
  rowCountError: string | null;
  uploading: boolean;
  onFileChosen: (file: File) => void;
  onNext: () => void;
}

function Step1Upload({
  parsing,
  parsedCsv,
  rowCountError,
  uploading,
  onFileChosen,
  onNext,
}: Step1Props) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.name.endsWith(".csv") && file.type !== "text/csv") return;
    onFileChosen(file);
  };

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        role="button"
        tabIndex={0}
        aria-label="CSV upload drop zone"
        className={[
          "border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors",
          dragging ? "border-primary bg-primary/5" : "border-white/[0.1] hover:bg-white/[0.02]",
        ].join(" ")}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
        }}
      >
        <UploadCloud className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40 flex-shrink-0" />
        <p className="text-sm font-medium text-foreground">Drop a CSV file here</p>
        <p className="text-xs text-muted-foreground mt-1">
          or <span className="text-primary underline cursor-pointer">browse</span> — max{" "}
          {ROW_COUNT_CAP.toLocaleString()} rows
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {/* Row-count error */}
      {rowCountError && (
        <div className="flex items-start gap-2 text-destructive border border-destructive/30 rounded-md p-3 bg-destructive/5">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p className="text-sm">{rowCountError}</p>
        </div>
      )}

      {/* Parsing state */}
      {parsing && (
        <div className="space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      )}

      {/* Parsed summary */}
      {parsedCsv && !rowCountError && !parsing && (
        <div className="border border-border rounded-md p-3 bg-card/40 space-y-1">
          <p className="text-sm font-medium">{parsedCsv.file.name}</p>
          <p className="text-xs text-muted-foreground">
            {parsedCsv.rows.length.toLocaleString()} rows · {parsedCsv.headers.length} columns
          </p>
          <div className="flex flex-wrap gap-1 pt-1">
            {parsedCsv.headers.slice(0, 8).map((h) => (
              <span key={h} className="font-mono text-xs bg-muted/40 rounded px-1.5 py-0.5">
                {h}
              </span>
            ))}
            {parsedCsv.headers.length > 8 && (
              <span className="text-xs text-muted-foreground">
                +{parsedCsv.headers.length - 8} more
              </span>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <Button onClick={onNext} disabled={!parsedCsv || !!rowCountError || parsing || uploading}>
          {uploading ? "Uploading…" : "Next: Map to Object Type"}
        </Button>
      </div>
    </div>
  );
}

// ── Step 2: Mapping (delegates to MappingWizard) ────────────────────────────

interface Step2Props {
  parsedCsv: ParsedCsv;
  dataSource: DataSource;
  autoAppliedMapping: Mapping | null;
  propertyTypes: PropertyType[];
  selectedObjectType: ObjectType | null;
  onObjectTypeSelected: (objectType: ObjectType, props: PropertyType[]) => void;
  onMappingSaved: (mapping: Mapping) => void;
}

function Step2Mapping({
  parsedCsv,
  dataSource,
  autoAppliedMapping,
  propertyTypes,
  selectedObjectType,
  onObjectTypeSelected,
  onMappingSaved,
}: Step2Props) {
  return (
    <MappingWizard
      headers={parsedCsv.headers}
      schema={parsedCsv.schema}
      dataSource={dataSource}
      autoAppliedMapping={autoAppliedMapping}
      selectedObjectType={selectedObjectType}
      propertyTypes={propertyTypes}
      onObjectTypeSelected={onObjectTypeSelected}
      onMappingSaved={onMappingSaved}
    />
  );
}

// ── Step 3: Preview (delegates to MaterializationPreview) ───────────────────

interface Step3Props {
  parsedCsv: ParsedCsv;
  mapping: Mapping;
  objectType: ObjectType;
  propertyTypes: PropertyType[];
  autoApplied: boolean;
  onEditMapping: () => void;
  onMaterialize: () => void;
}

function Step3Preview({
  parsedCsv,
  mapping,
  objectType,
  propertyTypes,
  autoApplied,
  onEditMapping,
  onMaterialize,
}: Step3Props) {
  return (
    <div className="space-y-4">
      {autoApplied && (
        <div className="flex items-start gap-2 text-sm border border-primary/30 rounded-md p-3 bg-primary/5">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
          <div className="flex-1">
            <span>Auto-applied mapping: </span>
            <strong>{mapping.name}</strong>.{" "}
            <button className="text-primary underline" onClick={onEditMapping}>
              Edit instead
            </button>
          </div>
        </div>
      )}

      <MaterializationPreview
        rows={parsedCsv.rows.slice(0, 5)}
        headers={parsedCsv.headers}
        mapping={mapping}
        propertyTypes={propertyTypes}
        objectType={objectType}
      />

      <div className="flex justify-between pt-2">
        <Button variant="outline" onClick={onEditMapping}>
          Back
        </Button>
        <Button onClick={onMaterialize}>
          Materialize {parsedCsv.rows.length.toLocaleString()} rows
        </Button>
      </div>
    </div>
  );
}

// ── Step 4: Materialize ─────────────────────────────────────────────────────

/** Exported for unit testing only — not part of the public API. */
export interface Step4Props {
  dataSource: DataSource;
  mapping: Mapping;
  rowCount: number;
  onDone: () => void;
}

/** Exported for unit testing only — not part of the public API. */
export function Step4Materialize({ dataSource, mapping, rowCount, onDone }: Step4Props) {
  // Async/polling pattern per ontology spec §6.
  // 1. User clicks Materialize → POST to materialize-objects → receive { materialization_id, status: 'pending' }
  // 2. Store materialization_id and poll via useMaterialization(id) every 5s while running.
  // 3. Render progress / success / failure from polled Materialization row.
  const [materializationId, setMaterializationId] = useState<string | null>(null);
  const [invokeError, setInvokeError] = useState<string | null>(null);
  const [invoking, setInvoking] = useState(false);

  const { materialization } = useMaterialization(materializationId);

  const handleMaterialize = useCallback(async () => {
    setInvoking(true);
    setInvokeError(null);

    try {
      const res = await invokeEdgeFunction<MaterializeObjectsResponse>("materialize-objects", {
        mapping_id: mapping.id,
        data_source_id: dataSource.id,
      });
      setMaterializationId(res.materialization_id);
    } catch (err) {
      setInvokeError((err as Error).message ?? "Unknown error");
    } finally {
      setInvoking(false);
    }
  }, [dataSource.id, mapping.id]);

  // ── Idle: waiting for user to click Materialize ──────────────────────────────
  if (!materializationId && !invokeError) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Ready to materialize <strong>{rowCount.toLocaleString()} rows</strong> into{" "}
          <strong>{mapping.name}</strong>.
        </p>
        <div className="flex justify-end">
          <Button onClick={handleMaterialize} disabled={invoking}>
            {invoking ? "Starting…" : `Materialize ${rowCount.toLocaleString()} rows`}
          </Button>
        </div>
      </div>
    );
  }

  // ── Invoke error (edge function call itself failed) ──────────────────────────
  if (invokeError) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2 text-destructive border border-destructive/30 rounded-md p-3 bg-destructive/5">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium">Materialization failed</p>
            <p className="text-xs mt-0.5">{invokeError}</p>
          </div>
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={onDone}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  // ── Polling: pending or running ──────────────────────────────────────────────
  if (
    !materialization ||
    materialization.status === "pending" ||
    materialization.status === "running"
  ) {
    const rowsIngested = materialization?.rows_inserted ?? 0;
    return (
      <div className="space-y-3" data-testid="materialize-processing">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-2/3" />
        <p className="text-xs text-muted-foreground">
          {rowsIngested > 0
            ? `Processing… ${rowsIngested.toLocaleString()} rows ingested so far`
            : "Processing…"}
        </p>
      </div>
    );
  }

  // ── Failed: background job reported failure ──────────────────────────────────
  if (materialization.status === "failed") {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2 text-destructive border border-destructive/30 rounded-md p-3 bg-destructive/5">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium">Materialization failed</p>
            <p className="text-xs mt-0.5">{materialization.error ?? "Unknown error"}</p>
          </div>
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={onDone}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  // ── Succeeded ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="materialize-success">
      <div className="flex items-start gap-3 border border-primary/30 rounded-md p-4 bg-primary/5">
        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
          <Check className="w-4 h-4 text-primary" />
        </div>
        <div>
          <p className="text-sm font-medium">Materialization complete</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {materialization.rows_inserted.toLocaleString()} rows inserted
            {materialization.rows_failed > 0 ? `, ${materialization.rows_failed} failed` : ""}
          </p>
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={onDone}>Done</Button>
      </div>
    </div>
  );
}
